<!-- HolidayManager.vue -->
<template>
    <div class="holiday-manager">
      <h3>Manage Holidays</h3>
      <button @click="showAddModal = true" class="add-button">Add New Holiday</button>
      
      <table class="holiday-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Start Date</th>
            <th>End Date</th>
            <th>Recurring</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="holiday in holidays" :key="holiday.id">
            <td>{{ holiday.name }}</td>
            <td>{{ formatDate(holiday.start_date) }}</td>
            <td>{{ formatDate(holiday.end_date) }}</td>
            <td>{{ holiday.repeats_annually ? 'Yes' : 'No' }}</td>
            <td>
              <button @click="editHoliday(holiday)" class="edit-button">Edit</button>
              <button @click="confirmDeleteHoliday(holiday.id)" class="delete-button">Delete</button>
            </td>
          </tr>
        </tbody>
      </table>
  
      <!-- Add/Edit Modal -->
      <div v-if="showAddModal" class="modal-overlay">
        <div class="modal-content">
          <h4>{{ editingHoliday ? 'Edit Holiday' : 'Add New Holiday' }}</h4>
          <form @submit.prevent="saveHoliday">
            <div class="form-group">
              <label>Holiday Name</label>
              <input type="text" v-model="holidayForm.name" required>
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label>Start Date</label>
                <input type="date" v-model="holidayForm.start_date" required>
              </div>
              <div class="form-group">
                <label>End Date</label>
                <input type="date" v-model="holidayForm.end_date" required>
              </div>
            </div>
            
            <div class="form-group checkbox-group">
              <input type="checkbox" v-model="holidayForm.repeats_annually" id="repeatsAnnually">
              <label for="repeatsAnnually">Repeats Annually</label>
            </div>
            
            <div class="form-group">
              <label>Description</label>
              <textarea v-model="holidayForm.description" rows="3"></textarea>
            </div>
            
            <div class="form-actions">
              <button type="button" @click="closeModal">Cancel</button>
              <button type="submit">Save</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { ref, onMounted } from 'vue';
  import dayjs from 'dayjs';
  
  export default {
    name: 'HolidayManager',
    props: {
      api: {
        type: Object,
        required: true
      }
    },
    setup(props, { emit }) {
      const holidays = ref([]);
      const showAddModal = ref(false);
      const editingHoliday = ref(null);
      const holidayForm = ref({
        name: '',
        start_date: '',
        end_date: '',
        repeats_annually: false,
        description: ''
      });
  
      const formatDate = (dateStr) => {
        return dayjs(dateStr).format('MMM D, YYYY');
      };
  
      const fetchHolidays = async () => {
        try {
          const response = await props.api.get('/holidays');
          holidays.value = response.data.data;
        } catch (error) {
          console.error('Error fetching holidays:', error);
        }
      };
  
      const saveHoliday = async () => {
        try {
          if (editingHoliday.value) {
            await props.api.put(`/holidays/${editingHoliday.value.id}`, holidayForm.value);
          } else {
            await props.api.post('/holidays', holidayForm.value);
          }
          await fetchHolidays();
          closeModal();
          emit('holiday-changed');
        } catch (error) {
          console.error('Error saving holiday:', error);
          alert('Error saving holiday: ' + (error.response?.data?.message || error.message));
        }
      };
  
      const editHoliday = (holiday) => {
        editingHoliday.value = holiday;
        holidayForm.value = {
          name: holiday.name,
          start_date: holiday.start_date,
          end_date: holiday.end_date,
          repeats_annually: holiday.repeats_annually,
          description: holiday.description
        };
        showAddModal.value = true;
      };
  
      const confirmDeleteHoliday = (id) => {
        if (confirm('Are you sure you want to delete this holiday?')) {
          deleteHoliday(id);
        }
      };
  
      const deleteHoliday = async (id) => {
        try {
          await props.api.delete(`/holidays/${id}`);
          await fetchHolidays();
          emit('holiday-changed');
        } catch (error) {
          console.error('Error deleting holiday:', error);
          alert('Error deleting holiday: ' + (error.response?.data?.message || error.message));
        }
      };
  
      const closeModal = () => {
        showAddModal.value = false;
        editingHoliday.value = null;
        holidayForm.value = {
          name: '',
          start_date: '',
          end_date: '',
          repeats_annually: false,
          description: ''
        };
      };
  
      onMounted(() => {
        fetchHolidays();
      });
  
      return {
        holidays,
        showAddModal,
        editingHoliday,
        holidayForm,
        formatDate,
        fetchHolidays,
        saveHoliday,
        editHoliday,
        confirmDeleteHoliday,
        deleteHoliday,
        closeModal
      };
    }
  };
  </script>
  
  <style scoped>
  .holiday-manager {
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    margin-top: 20px;
  }
  
  .holiday-manager h3 {
    margin-top: 0;
    color: #2c3e50;
    border-bottom: 1px solid #eee;
    padding-bottom: 10px;
  }
  
  .add-button {
    background-color: #42b983;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    margin-bottom: 15px;
    cursor: pointer;
  }
  
  .holiday-table {
    width: 100%;
    border-collapse: collapse;
  }
  
  .holiday-table th, .holiday-table td {
    padding: 10px;
    text-align: left;
    border-bottom: 1px solid #eee;
  }
  
  .holiday-table th {
    background-color: #f5f5f5;
  }
  
  .edit-button {
    background-color: #2196F3;
    color: white;
    border: none;
    padding: 5px 10px;
    border-radius: 3px;
    margin-right: 5px;
    cursor: pointer;
  }
  
  .delete-button {
    background-color: #f44336;
    color: white;
    border: none;
    padding: 5px 10px;
    border-radius: 3px;
    cursor: pointer;
  }
  
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0,0,0,0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  
  .modal-content {
    background: white;
    padding: 20px;
    border-radius: 8px;
    width: 500px;
    max-width: 90%;
  }
  
  .modal-content h4 {
    margin-top: 0;
  }
  
  .form-group {
    margin-bottom: 15px;
  }
  
  .form-group label {
    display: block;
    margin-bottom: 5px;
  }
  
  .form-group input[type="text"],
  .form-group input[type="date"],
  .form-group textarea {
    width: 100%;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
  }
  
  .form-row {
    display: flex;
    gap: 15px;
  }
  
  .checkbox-group {
    display: flex;
    align-items: center;
  }
  
  .checkbox-group input {
    margin-right: 8px;
  }
  
  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
  }
  
  .form-actions button {
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
  }
  
  .form-actions button[type="button"] {
    background-color: #f0f0f0;
    border: 1px solid #ddd;
  }
  
  .form-actions button[type="submit"] {
    background-color: #42b983;
    color: white;
    border: none;
  }
  </style>